import React, { ReactElement, useState, useCallback, useMemo } from 'react';
import IconCheckBold from '~icons/mdi/check-bold';
import Box from '@mui/material/Box';
import ReactMarkdown from 'react-markdown';
import './pulse-anim.css';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

interface Props {
  isCorrectResponse: boolean;
  isSelected: boolean;
  onReviewed?: () => void;
  className?: string;
  reason?: string | null;
  /**
   * When showing tooltips in a component that uses a transition to
   * first render, that component must tell us when it is in its final
   * rendered position, so that the tooltip can decide *where* to appear.
   * Only important for tooltips that auto-appear on first render,
   * such as the "Click Review" prompt.
   * @default true
   */
  canRenderTooltip?: boolean;
  tipShouldOpen?: boolean;
  hasRequestReviewPrompt?: boolean;
  /**
   * If true, component should be rendered in a de-emphasized style. Used for examples in teacher
   * dashboard for responses not related to tags in a card. (used for multi-part questions)
   */
  deemphasize?: boolean;
}

type ReviewTooltipSlotProps = TooltipProps['slotProps'];
type ReviewPhase = 'prompt' | 'feedback' | 'none';

const PROMPT_TEXT = 'Click/Tap on each Review button to see further explanation.';

const promptTooltipSlotProps: ReviewTooltipSlotProps = {
  tooltip: {
    sx: {
      color: 'text.primary',
      bgcolor: 'background.paper',
      outlineStyle: 'solid',
      outlineWidth: '1px',
      width: '19ch',
      fontSize: '1em',
    },
  },
  arrow: { sx: { color: 'text.primary' } },
};

const feedbackTooltipSlotProps = (outlineColor: string): ReviewTooltipSlotProps => ({
  tooltip: {
    sx: {
      outlineColor,
      color: 'text.primary',
      bgcolor: 'background.paper',
      outlineStyle: 'solid',
      outlineWidth: '2px',
      maxWidth: 'calc(min(15rem, 60vw))',
      fontSize: '0.8rem'
    },
  },
  arrow: { sx: { color: outlineColor } },
});

export const Review = (props: Props): ReactElement => {
  const {
    isCorrectResponse,
    isSelected,
    reason = null,
    onReviewed,
    canRenderTooltip = true,
    tipShouldOpen = false,
    hasRequestReviewPrompt = false,
    deemphasize = false
  } = props;

  // Keep track of whether they reviewed an option, disable pulse animation if they did already
  const [hasReviewed, setHasReviewed] = useState(false);

  const [reviewPhase, setReviewPhase] = useState<ReviewPhase>(
    hasRequestReviewPrompt ? 'prompt' : tipShouldOpen ? 'feedback' : 'none'
  );

  const onReviewClicked = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const newPhase =
        reviewPhase === 'feedback'
          ? 'none'
          : 'feedback';
      if (newPhase === 'feedback' && !hasReviewed) {
        onReviewed?.();
        setHasReviewed(true);
      }
      setReviewPhase(newPhase);
      event.stopPropagation();
    },
    [hasReviewed, reviewPhase]
  );

  const isCorrect = isCorrectResponse === isSelected;

  const theme = useTheme();
  let color = isCorrectResponse
    ? theme.palette.success.light
    : theme.palette.error.light;
  if (deemphasize) {
    color = theme.palette.grey[100]
  }

  const title = useMemo(
    () =>
      reviewPhase === 'prompt'
        ? PROMPT_TEXT
        : reason && !isCorrect && <ReactMarkdown>{reason}</ReactMarkdown>,
    [reason, isCorrect, reviewPhase]
  );

  const slotProps = useMemo(
    () =>
      reviewPhase === 'prompt'
        ? promptTooltipSlotProps
        : feedbackTooltipSlotProps(color),
    [reviewPhase, color]
  );

  const handleTooltipClose = useCallback(() => setReviewPhase('none'), []);

  return (!isCorrect || isCorrectResponse) ? (
    <Tooltip
      open={(reviewPhase !== 'none') && canRenderTooltip && reason != null}
      onClose={handleTooltipClose}
      placement="right"
      slotProps={slotProps}
      leaveTouchDelay={100000}
      disableHoverListener
      arrow
      title={title}
    >
      <Box
        tabIndex={isCorrect ? undefined : 0}
        onClick={onReviewClicked}
        sx={{
          display: 'flex',
          alignSelf: 'stretch',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '70px',
          backgroundColor: color,
          color: 'black',
          p: 1,
          animation:
            !isCorrect && reason && !hasReviewed
              ? 'pulse 3s ease-out infinite'
              : '',
          animationDelay: '2s',
          borderRadius: '0 5px 4px 0',
          pointerEvents: !reason ? 'none' : 'all',
          cursor: isCorrect ? 'default' : 'pointer',
        }}
      >
        {isCorrect ? (
          <IconCheckBold />
        ) : reason ? (
          <span>Review</span>
        ) : isSelected ? (
          'Incorrect'
        ) : (
          'Answer'
        )}
      </Box>
    </Tooltip>
  ) : (
    <></>
  );
};
